import { useState } from "react";
import { Row, Col } from "react-bootstrap";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { setChannel } from "../../../redux/reducers/channel";
import { updateAmzOnboardingStatus } from "../../../redux/reducers/amzOnboardReducer";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { useWalmartOnboardingStatus } from "./useWalmartOnboardingStatus";
import { useWalmartMarketplaceStatus } from "./useWalmartMarketplaceStatus";
import { initWalmartMpOauth } from "./initWalmartOAuth";
import DialogueCard from "../components/DialogueCard";
import SkipToDashboardLink from "../components/SkipToDashboardLink";
import { WalmartAdvertisingConnect } from "./WalmartAdvertisingConnect";
import { SECONDARY_CHANNEL_STORAGE_KEY, CHANNEL_STORAGE_KEY } from "../shared/constants";
import { resetDocumentTitle } from "../shared/utils";
import { Button } from "../../../components/core/basic/Button";
import { Center } from "../../../../styled-system/jsx";
import { useMarkWalmartOnboardingStep } from "./api/useMarkWalmartOnboardingStep";
import { css } from "../../../../styled-system/css";

export const WalmartApiConnections = () => {
  const [selectedCard, setSelectedCard] = useState<number | null>(null);
  const { t } = useTranslation(["onboarding", "common", "auth"]);

  const user = useAppSelector((state) => state.user);
  const { organization_type: orgType, organization_id: orgId } = user;
  const amz_onboard = useAppSelector((state) => state.amz_onboard);
  const errors = useAppSelector((state) => state.errors);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // Picked on the seller-type step, which passes it along in the URL.
  const sellerType = useSearch({ strict: false, select: (s) => s.sellerType });

  const { data: onboardingStatus } = useWalmartOnboardingStatus();
  const { data: marketplaceStatus } = useWalmartMarketplaceStatus();

  const walmartAdvertisingConnected =
    onboardingStatus?.partner_access_state === "connected" && !!onboardingStatus.advertiser_id_provided;
  // A forced save leaves access unconfirmed, which is not worth a checkmark but must not block the
  // rest of onboarding -- waiting out Walmart's propagation is exactly what forcing opts out of.
  const walmartAdvertisingPending =
    onboardingStatus?.partner_access_state === "pending" && !!onboardingStatus.advertiser_id_provided;
  const walmartAdvertisingSaved = walmartAdvertisingConnected || walmartAdvertisingPending;
  const walmartMarketplaceCredentialsProvided = marketplaceStatus?.is_connected;

  const triggerWalmartOnboardingTasks = useMarkWalmartOnboardingStep();

  const handleApiTasks = async () => {
    resetDocumentTitle();

    const walmartSecondaryChannelOnboardingFlag = localStorage[SECONDARY_CHANNEL_STORAGE_KEY] === "walmart";

    try {
      if (walmartSecondaryChannelOnboardingFlag) {
        await triggerWalmartOnboardingTasks.mutateAsync({
          action: "mark_secondary_channel_onboarding_complete",
        });

        dispatch(setChannel(localStorage[SECONDARY_CHANNEL_STORAGE_KEY]));

        localStorage.setItem(CHANNEL_STORAGE_KEY, localStorage[SECONDARY_CHANNEL_STORAGE_KEY]);
        localStorage.removeItem(SECONDARY_CHANNEL_STORAGE_KEY);
      } else {
        await triggerWalmartOnboardingTasks.mutateAsync({ action: "mark_onboarding_complete" });
      }
    } catch {
      return;
    }

    if (!["active", "trial_active", "qore_active"].includes(amz_onboard.org_status)) {
      dispatch(updateAmzOnboardingStatus({ ...amz_onboard, org_status: "pre_trial" }));
    }

    void navigate({ to: "/onboarding/v2/summary" });
  };

  const showContinueButton =
    walmartAdvertisingSaved &&
    ((orgType === "seller" && walmartMarketplaceCredentialsProvided) ||
      orgType === "vendor" ||
      sellerType === "vendor");

  return (
    <Row className="my-5">
      <Col xs={12} lg={{ offset: 2, span: 8 }}>
        <h2 className="mt-5 mb-1 fw-600">{t(($) => $.onboarding["Connect Walmart"])}</h2>
        <p className="fs-4">
          {t(($) => $.onboarding["We require access to both the Walmart Marketplace API and Walmart Advertising API"])}
        </p>
      </Col>

      <Col xs={12} lg={{ offset: 2, span: 8 }}>
        {errors.length ?
          errors.map((error: unknown) => {
            const err = error as { message?: string; code?: string };
            const message =
              err.code === "SESSION_EXPIRED" ?
                t(($) => $.auth["Your session has expired. Please login again."])
              : err.message;
            return (
              <p key={message} className="mt-5 ps-3 fs-5 invalid-feedback d-block text-center">
                {message}
              </p>
            );
          })
        : null}
        <div
          className={css({
            display: "flex",
            gap: "medium",
            flexDirection: "column",
          })}
        >
          <DialogueCard
            title={t(($) => $.onboarding["Walmart Advertising API"])}
            description=""
            justify="center"
            onPress={() => {
              setSelectedCard(0 === selectedCard ? null : 0);
            }}
            successIcon={walmartAdvertisingConnected}
            pendingIcon={walmartAdvertisingPending}
            statusLabel={
              walmartAdvertisingPending ? t(($) => $.onboarding["Saved, waiting for Walmart to confirm access"])
              : walmartAdvertisingConnected ?
                t(($) => $.onboarding["Connected"])
              : undefined
            }
          >
            {selectedCard === 0 && (
              <WalmartAdvertisingConnect
                onConnected={() => {
                  setSelectedCard(1);
                }}
                sellerType={sellerType}
              />
            )}
          </DialogueCard>

          {!(orgType === "vendor" || onboardingStatus?.seller_type === "vendor") && (
            <DialogueCard
              title={t(($) => $.onboarding["Walmart Marketplace API"])}
              justify="center"
              onPress={() => {
                setSelectedCard(1 === selectedCard ? null : 1);
                void initWalmartMpOauth(orgId, {
                  title: t(($) => $.onboarding["Walmart connection failed"]),
                  message: t(($) => $.onboarding["Could not start the Walmart connection. Please try again."]),
                });
              }}
              successIcon={walmartMarketplaceCredentialsProvided}
            />
          )}
        </div>
        <div
          className={css({
            marginTop: "medium",
          })}
        >
          {showContinueButton ?
            <Center>
              <Button
                onPress={async () => {
                  await handleApiTasks();
                }}
                width={"100%"}
              >
                {t(($) => $.common.actions["Continue"])}
              </Button>
            </Center>
          : <SkipToDashboardLink onClick={async () => await handleApiTasks()} />}
        </div>
      </Col>
    </Row>
  );
};
